<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
    allTags: { type: Array, default: () => [] },
    tagCounts: { type: Object, default: () => ({}) },
    reservationForms: { type: Array, default: () => [] },
    filters: { type: Object, default: () => ({}) },
})

const emit = defineEmits([
    'toggle-tag', 'toggle-form',
    'update:date-from', 'update:date-to',
    'update:activity-from', 'update:activity-to',
    'reset',
])

const TAG_COLORS = [
    '#6366f1', '#ec4899', '#f59e0b', '#10b981',
    '#3b82f6', '#ef4444', '#8b5cf6', '#14b8a6',
    '#f97316', '#84cc16',
]

function getTagColor(index) {
    return TAG_COLORS[index % TAG_COLORS.length]
}

function isTagActive(tag) {
    return (props.filters.tags ?? []).includes(tag)
}

function isFormActive(id) {
    return (props.filters.form_ids ?? []).map(Number).includes(id)
}

const MAX_VISIBLE = 6
const showAllTags = ref(false)
const showAllForms = ref(false)

const visibleTags = computed(() =>
    showAllTags.value ? props.allTags : props.allTags.slice(0, MAX_VISIBLE)
)
const hiddenTagsCount = computed(() => Math.max(0, props.allTags.length - MAX_VISIBLE))

const visibleForms = computed(() =>
    showAllForms.value ? props.reservationForms : props.reservationForms.slice(0, MAX_VISIBLE)
)
const hiddenFormsCount = computed(() => Math.max(0, props.reservationForms.length - MAX_VISIBLE))

const hasActiveFilters = computed(() =>
    !!(props.filters.tags?.length || props.filters.form_ids?.length ||
       props.filters.date_from || props.filters.date_to ||
       props.filters.activity_from || props.filters.activity_to)
)

</script>

<template>
    <div class="bg-white dark:bg-black overflow-hidden">
        <div class="flex flex-wrap gap-x-8 gap-y-4">

            <!-- Tagy -->
            <div v-if="allTags.length" class="flex flex-col gap-1 min-w-40">
                <div class="flex items-center gap-2 mb-0.5">
                    <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{{ $t('contacts.filter_tags') }}</span>
                    <span class="bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-300 rounded px-1.5 py-px text-[10px] font-semibold">{{ allTags.length }}</span>
                </div>
                <div
                    v-for="(tag, i) in visibleTags"
                    :key="tag"
                    class="flex items-center gap-2 px-2 py-1 rounded-lg cursor-default hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors"
                    :class="isTagActive(tag) ? '' : 'opacity-50'"
                    @click="emit('toggle-tag', tag)"
                >
                    <div class="w-2 h-2 rounded-sm flex-shrink-0" :style="{ background: getTagColor(i) }" />
                    <span class="flex-1 text-[12.5px] text-neutral-800 dark:text-neutral-200 font-medium truncate">{{ tag }}</span>
                    <span v-if="tagCounts[tag]" class="text-[11px] text-neutral-400 tabular-nums flex-shrink-0">{{ tagCounts[tag] }}</span>
                    <i class="!text-[11px] flex-shrink-0 w-3" :class="isTagActive(tag) ? 'pi pi-check text-blue-500' : ''" />
                </div>
                <button
                    v-if="hiddenTagsCount > 0"
                    class="flex items-center gap-1 px-2 py-1 text-[11.5px] text-blue-500 font-medium hover:text-blue-700 rounded-lg transition-colors"
                    @click="showAllTags = !showAllTags"
                >
                    {{ showAllTags ? $t('contacts.filter_less') : $t('contacts.filter_more', { count: hiddenTagsCount }) }}
                </button>
            </div>

            <!-- Formuláře -->
            <div v-if="reservationForms.length" class="flex flex-col gap-1 min-w-40">
                <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-0.5">{{ $t('contacts.filter_forms') }}</span>
                <div
                    v-for="form in visibleForms"
                    :key="form.id"
                    class="flex items-center gap-2 px-2 py-1 rounded-lg cursor-default hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors"
                    :class="isFormActive(form.id) ? '' : 'opacity-50'"
                    @click="emit('toggle-form', form.id)"
                >
                    <i class="pi pi-file-o !text-[11px] text-neutral-400 flex-shrink-0" />
                    <span class="flex-1 text-[12.5px] text-neutral-800 dark:text-neutral-200 font-medium truncate">{{ form.name }}</span>
                    <i class="!text-[11px] flex-shrink-0 w-3" :class="isFormActive(form.id) ? 'pi pi-check text-blue-500' : ''" />
                </div>
                <button
                    v-if="hiddenFormsCount > 0"
                    class="flex items-center gap-1 px-2 py-1 text-[11.5px] text-blue-500 font-medium hover:text-blue-700 rounded-lg transition-colors"
                    @click="showAllForms = !showAllForms"
                >
                    {{ showAllForms ? $t('contacts.filter_less') : $t('contacts.filter_more', { count: hiddenFormsCount }) }}
                </button>
            </div>

            <!-- Datumy -->
            <div class="flex flex-col gap-3">
                <!-- Registrace -->
                <div class="flex flex-col gap-1">
                    <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{{ $t('contacts.filter_registration') }}</span>
                    <div class="flex items-center gap-1.5">
                        <input type="date" :value="filters.date_from ?? ''"
                            class="text-[12px] bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl px-2 py-1.5 text-neutral-700 dark:text-neutral-200 outline-none focus:ring-1 focus:ring-blue-500 w-32"
                            @change="emit('update:date-from', $event.target.value)" />
                        <span class="text-neutral-300 text-xs">—</span>
                        <input type="date" :value="filters.date_to ?? ''"
                            class="text-[12px] bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl px-2 py-1.5 text-neutral-700 dark:text-neutral-200 outline-none focus:ring-1 focus:ring-blue-500 w-32"
                            @change="emit('update:date-to', $event.target.value)" />
                    </div>
                </div>
                <!-- Poslední aktivita -->
                <div class="flex flex-col gap-1">
                    <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{{ $t('contacts.filter_last_activity') }}</span>
                    <div class="flex items-center gap-1.5">
                        <input type="date" :value="filters.activity_from ?? ''"
                            class="text-[12px] bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl px-2 py-1.5 text-neutral-700 dark:text-neutral-200 outline-none focus:ring-1 focus:ring-blue-500 w-32"
                            @change="emit('update:activity-from', $event.target.value)" />
                        <span class="text-neutral-300 text-xs">—</span>
                        <input type="date" :value="filters.activity_to ?? ''"
                            class="text-[12px] bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl px-2 py-1.5 text-neutral-700 dark:text-neutral-200 outline-none focus:ring-1 focus:ring-blue-500 w-32"
                            @change="emit('update:activity-to', $event.target.value)" />
                    </div>
                </div>
            </div>

            <!-- Reset -->
            <div class="flex flex-col gap-2 justify-start ml-auto">
                <!-- Reset -->
                <button
                    v-if="hasActiveFilters"
                    class="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-500 font-medium hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl transition-colors"
                    @click="emit('reset')"
                >
                    <i class="pi pi-filter-slash !text-xs" />{{ $t('contacts.filter_reset') }}
                </button>
            </div>

        </div>
    </div>
</template>
